import { useTranslation } from "react-i18next";
import { ChevronRight, Code2 } from "lucide-react";
import { ClickToCopyId } from "@/components/common";
import { Card } from "@/components/ui";
import type { AdminBookingDetailResponse } from "@/services/bookingService";
import { economicEventTypeLabel } from "@/lib/economicEventLabels";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import type { BookingListRow } from "./bookingViewModel";

interface BookingAdminTechnicalRefsProps {
  row: BookingListRow;
  detail: AdminBookingDetailResponse;
}

interface TechnicalRef {
  label: string;
  value: string | null | undefined;
}

/** Collapsed-by-default list of copyable resource IDs (support / developers). */
export function BookingAdminTechnicalRefs({
  row,
  detail,
}: BookingAdminTechnicalRefsProps) {
  const { t } = useTranslation("bookings");
  const { t: tPayments } = useTranslation("payments");
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const booking = detail.booking;
  const payment = hasPermission(Permission.EXTERNAL_PAYMENTS_LIST)
    ? detail.external_payment
    : null;
  const economicEvents = hasPermission(Permission.ECONOMIC_EVENTS_LIST)
    ? detail.economic_events
    : [];

  const refs: TechnicalRef[] = [
    { label: t("detail.bookingId"), value: row.id },
    { label: t("adminDetail.partner.activity"), value: booking.activity_id },
    {
      label: t("adminDetail.partner.name"),
      value: detail.partner?.user_id ?? detail.activity.user_id,
    },
    { label: t("detail.hostingStructure"), value: booking.house_id },
    {
      label: t("adminDetail.structure.host"),
      value: booking.host_user_id ?? detail.host?.user_id,
    },
    {
      label: t("adminDetail.salesAgent.name"),
      value: detail.sales_agent_activity?.sales_agent_user_id,
    },
    { label: t("detail.generatingDevice"), value: booking.device_id },
    { label: t("adminDetail.ids.handshakeSession"), value: row.handshakeSessionId },
    { label: t("adminDetail.ids.externalPayment"), value: payment?.id },
    {
      label: t("adminDetail.payment.providerId"),
      value: payment?.provider_payment_id,
    },
    ...economicEvents.map((event) => ({
      label: economicEventTypeLabel(event.type, tPayments),
      value: event.id,
    })),
  ];
  const visibleRefs = refs.filter((ref): ref is { label: string; value: string } =>
    Boolean(ref.value),
  );

  return (
    <Card className="overflow-hidden">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50 [&::-webkit-details-marker]:hidden">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-surface-200 bg-surface-50 text-surface-500">
            <Code2 className="h-4 w-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold tracking-tight text-surface-900">
              {t("adminDetail.ids.title")}
            </span>
            <span className="block text-xs text-surface-500">
              {t("adminDetail.ids.hint")}
            </span>
          </span>
          <ChevronRight
            className="h-4 w-4 shrink-0 text-surface-400 transition-transform group-open:rotate-90"
            aria-hidden
          />
        </summary>
        <dl className="divide-y divide-surface-200/70 border-t border-surface-200/70">
          {visibleRefs.map((ref) => (
            <div key={`${ref.label}-${ref.value}`} className="px-5 py-2.5">
              <dt className="text-xs font-medium text-surface-500">{ref.label}</dt>
              <dd className="mt-0.5 min-w-0">
                <ClickToCopyId value={ref.value} className="text-xs text-surface-900" />
              </dd>
            </div>
          ))}
        </dl>
      </details>
    </Card>
  );
}

